import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Form for rule_type='upgrade_count_per_tier' or 'maintain_count_per_tier' (D-038).
 * rule_value shape: { per_tier: { 1: a, 2: b, ..., 10: j } }
 * 10 inputs (T1..T10) — counts of creators expected to rank-up / maintain per tier.
 *
 * Same shape as TierBasedRuleForm, kept separate for clarity / future divergence.
 */
const TIERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const CountPerTierRuleForm = ({ value, onChange, disabled, variant = 'upgrade' }) => {
  const { t } = useTranslation();
  const perTier = value?.per_tier ?? {};

  const handleTier = (tier, raw) => {
    const next = { ...perTier };
    if (raw === '' || raw === null) {
      delete next[tier];
    } else {
      next[tier] = Number(raw);
    }
    onChange({ per_tier: next });
  };

  const helpKey =
    variant === 'maintain'
      ? 'admin.objectiveRules.form.countPerTier.maintainHelp'
      : 'admin.objectiveRules.form.countPerTier.upgradeHelp';
  const helpFallback =
    variant === 'maintain'
      ? 'Number of creators expected to maintain per tier this month.'
      : 'Number of creators expected to rank-up per tier this month.';

  return (
    <div className="space-y-2">
      <label className="block text-xs uppercase tracking-widest text-muted-foreground font-semibold">
        {t(
          'admin.objectiveRules.form.countPerTier.title',
          'Count per tier (T1 → T10)'
        )}
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {TIERS.map((tier) => (
          <div key={tier} className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              T{tier}
            </label>
            <input
              type="number"
              step="1"
              min="0"
              disabled={disabled}
              value={perTier[tier] ?? perTier[String(tier)] ?? ''}
              onChange={(e) => handleTier(tier, e.target.value)}
              className="w-full px-2 py-1.5 rounded-md bg-card border border-border text-foreground text-base tabular-nums outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 disabled:opacity-60"
              placeholder="0"
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t(helpKey, helpFallback)}</p>
    </div>
  );
};

export default CountPerTierRuleForm;
